import { format } from 'date-fns'
import React, { ReactElement } from 'react'
import { Trans } from 'react-i18next'

import { isValidUrl } from '../../common/helpers.js'
import { CommonSubscriptionLisaAndProduct } from '../../common/models/common-subscription-lisa-and-product.js'
import { SubscriptionProductPlan } from '../../common/models/subscription-product-plan.js'
import { CalculationResponse, CustomerDTO, SubscriptionDTO, SubscriptionProductDTO } from '../../common/types.js'
import CommonLisaAndInterface from '../../common/utils/lisa-and-api.js'
import { formatMonthlyPrice } from '../../common/utils/utils.js'
import { PlanCardProps } from '../components-hydrated/plan-cards/plan-cards.js'
import { CartGridRowProps } from '../components/cart/cart.js'
import { FlexLogo } from '../components/flex-logo/flex-logo.js'
import { FlexIcon } from '../components/icon/icon.js'
import { Image, ImageProps } from '../components/image/image.js'
import { ListItemProps } from '../components/list/list.js'
import { ProductProps } from '../components/product/product.js'
import i18n from '../i18n.js'
import { Customer } from './customer.js'
import { getCommonSubscriptionCardDetailListItems } from './subscription-product-common.js'
import { Subscription } from './subscription.js'

export class SubscriptionLisaAndProduct extends CommonSubscriptionLisaAndProduct {
    public plan: SubscriptionProductPlan
    public subscription: Subscription
    public customer: Customer

    constructor(
        product: SubscriptionProductDTO,
        currencySymbol: string,
        locale: string,
        currentPlan: SubscriptionProductPlan,
        priceData: CalculationResponse,
        customer?: CustomerDTO,
    ) {
        super(product, currencySymbol, locale, priceData, currentPlan)

        this.plan = currentPlan

        if (customer) {
            this.customer = new Customer(customer)
        }
    }

    image(width = 40): JSX.Element {
        const url = isValidUrl(this.data.product?.imageUrl)
        if (url) {
            const imageProps: ImageProps = {
                role: 'presentation',
                srcset: `${url.toString()} ${width}w, ${url.toString()} ${width * 2}w`,
                width,
            }

            return <Image {...imageProps} className="product__image" />
        }

        return <FlexLogo width={width} />
    }

    get priceDescription(): string | ReactElement {
        if (Math.round(this.totalMonthlyDiscount) > 0) {
            if (this.plan.code === 'FLEX') {
                return (
                    <p className="text-tiny">
                        {i18n.t(`lisaAnd.plans.FLEX.priceDescription`, {
                            months: this.discountDuration,
                            price: formatMonthlyPrice(this.baseSubscriptionCostGross, this.currencySymbol, this.locale),
                        })}
                    </p>
                )
            }

            return (
                <p>
                    <s className="text-tiny">
                        {formatMonthlyPrice(this.baseSubscriptionCostGross, this.currencySymbol, this.locale)}
                    </s>
                </p>
            )
        }

        return ''
    }

    planCard(): PlanCardProps {
        return {
            type: 'minimal',
            title: i18n.t(`lisaAnd.plans.${this.plan.code}.title`, {
                months: this.plan.duration,
            }),
            description: this.renderDescription(),
            price: this.displayedPrice,
            priceDescription: this.priceDescription,
            children: '',
            id: `plan-selection-${this.plan.uuid}`,
            labelText: i18n.t(`plans.plans.${this.plan.code}.title`, {
                months: this.plan.duration,
            }),
            width: 'sm-12',
            value: this.plan.id,
        }
    }

    get cartRows() {
        const rows: CartGridRowProps[] = []
        const monthlyPrice = this.displayedPriceNumber
        const hasDiscounts = this.totalMonthlyTradeInDiscount > 0 || this.totalMonthlyGenericDiscount > 0

        rows.push({
            left: {
                text: this.data.product?.translation?.name,
                // text: i18n.t(`lisaAnd.cart.${this.plan.code}.monthlyCost`, {
                //     product: this.data.product?.translation?.name,
                //     months: this.discountDuration || 12,
                //     lng: this.locale,
                // }),
                weight: 'bold',
            },
            right: {
                text: formatMonthlyPrice(this.displayedPriceNumber, this.currencySymbol, this.locale),
                isBlue: true,
            },
        })

        rows.push({
            left: {
                text: (
                    <span className="h-offset">
                        {i18n.t(`lisaAnd.cart.${this.plan.code}.planName`, {
                            lng: this.locale,
                        })}
                    </span>
                ),
                weight: 'regular',
                size: 'small',
            },
            right: hasDiscounts
                ? {
                      text: (
                          <s>{formatMonthlyPrice(this.baseSubscriptionCostGross, this.currencySymbol, this.locale)}</s>
                      ),
                      weight: 'regular',
                      size: 'small',
                  }
                : undefined,
        })

        if (hasDiscounts) {
            rows.push({
                left: {
                    text: i18n.t('lisaAnd.cart.showDiscount', {
                        lng: this.locale,
                    }),
                    weight: 'regular',
                    size: 'small',
                },
            })

            rows.push({
                left: {
                    text: (
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore ReactI18N type definition issue
                        <Trans i18nKey="lisaAnd.cart.totalMonthlyDiscount" values={{ months: this.discountDuration }}>
                            <span>
                                Total monthly discount:
                                <strong
                                    dangerouslySetInnerHTML={{
                                        __html: formatMonthlyPrice(
                                            this.totalMonthlyDiscount,
                                            this.currencySymbol,
                                            this.locale,
                                        ),
                                    }}
                                ></strong>
                            </span>
                        </Trans>
                    ),
                    weight: 'regular',
                    size: 'small',
                },
            })

            if (this.totalMonthlyTradeInDiscount > 0 && this.totalMonthlyGenericDiscount > 0) {
                rows.push({
                    left: {
                        text: (
                            <Trans i18nKey="lisaAnd.cart.totalMonthlyDiscountIncludesBoth">
                                <span>This includes both discounts</span>
                            </Trans>
                        ),
                        weight: 'regular',
                        size: 'tiny',
                    },
                })
            }
        }

        return {
            monthly: monthlyPrice,
            rows,
        }
    }

    renderDescription() {
        return <div>{i18n.t(`lisaAnd.plans.${this.plan.code}.description`)}</div>
    }

    getProductCard(): ProductProps {
        return {
            content: 'Product card',
        }
    }

    getSubscriptionCardListItems() {
        const items: ListItemProps[] = [
            {
                children: (
                    <strong>
                        {i18n.t(`plans.plans.${this.plan.code}.title`, {
                            months: this.planDuration,
                        })}
                    </strong>
                ),
                icon: FlexIcon,
            },
        ]
        if (this.subscription.hasEnded) {
            items.push({
                children: (
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore ReactI18N type definition issue
                    <Trans i18nKey="subscription.endedDate">
                        Ended{' '}
                        <strong
                            dangerouslySetInnerHTML={{
                                __html: this.subscription.formattedContractEndDate,
                            }}
                        ></strong>
                    </Trans>
                ),
            })
        } else {
            if (this.subscription.formattedUpgradeDate) {
                items.push({
                    children: (
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore ReactI18N type definition issue
                        <Trans i18nKey="subscription.upgradeFrom">
                            Eligible for upgrade from{' '}
                            <strong
                                dangerouslySetInnerHTML={{
                                    __html: this.subscription.formattedUpgradeDate,
                                }}
                            ></strong>
                        </Trans>
                    ),
                })
            }

            items.push({
                children: <strong>{this.displayedPrice}</strong>,
            })
        }

        return items
    }

    getSubscriptionCardDetailListItems() {
        return getCommonSubscriptionCardDetailListItems(
            this,
            <strong>
                {i18n.t(`plans.plans.${this.plan.code}.title`, {
                    months: this.planDuration,
                })}
            </strong>,
            false,
            this.subscription.data.upgradeDate
                ? format(new Date(this.subscription.data.upgradeDate), 'dd.MM.yyyy')
                : undefined,
        )
    }
}

export const createLisaAndProducts = async (subscription: SubscriptionDTO) => {
    if (!subscription.products) {
        return []
    }

    const data = await CommonLisaAndInterface.getProductPrices(subscription)

    return subscription.products
        .map((product) => {
            if (!product.planPrice || !subscription.currency || !subscription.locale) {
                return null
            }

            return new SubscriptionLisaAndProduct(
                product,
                subscription.currency.code || '',
                subscription.locale.code || '',
                new SubscriptionProductPlan(
                    product.planPrice,
                    subscription.locale.code || '',
                    product.durationInMonths || 0,
                ),
                data,
                subscription.customer,
            )
        })
        .filter(Boolean)
}
